import { ExpandMore, Search } from '@mui/icons-material'
import { Box, Checkbox, Collapse, InputAdornment, Link, TextField, Typography, styled } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedSearchInput } from 'src/hooks/useDebouncedSearchInput'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  FlattenedScope,
  ScopeLocality,
  ScopeSection,
  buildScopeSections,
  countSelectedIn,
  filterSectionsByQuery,
  getAllLocalityIds,
  getGroupSelectionState,
  setGroupSelection,
  toggleLocalityId,
} from './unifiedReportGenerator.action'

interface ScopeSelectorProps {
  scope: FlattenedScope
  selectedIds: Set<number>
  onChange: (next: Set<number>) => void
}

export const ScopeSelector: React.FC<ScopeSelectorProps> = ({ scope, selectedIds, onChange }) => {
  const { t } = useTranslation('reportGenerator')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  const debouncedSearchQuery = useDebouncedSearchInput(searchQuery)

  const sections = useMemo(() => buildScopeSections(scope), [scope])
  const visibleSections = useMemo(
    () => filterSectionsByQuery(sections, debouncedSearchQuery),
    [sections, debouncedSearchQuery],
  )
  const allLocalityIds = useMemo(() => getAllLocalityIds(scope), [scope])
  const totalCount = allLocalityIds.length
  const isSearching = debouncedSearchQuery.trim().length > 0
  const allState = getGroupSelectionState(selectedIds, allLocalityIds)

  const handleLocalityToggle = (id: number) => onChange(toggleLocalityId(selectedIds, id))

  const handleAllToggle = () => onChange(allState === 'all' ? new Set<number>() : new Set(allLocalityIds))

  const handleSectionToggle = (localities: ScopeLocality[]) => {
    const ids = localities.map(locality => locality.id)
    const selectAll = getGroupSelectionState(selectedIds, ids) !== 'all'
    onChange(setGroupSelection(selectedIds, ids, selectAll))
  }

  const handleExpandToggle = (key: string) => {
    const next = new Set(expandedKeys)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    setExpandedKeys(next)
  }

  if (sections.length === 0) {
    return (
      <EmptyState>
        <Typography variant="body2">{t('noLocalities')}</Typography>
      </EmptyState>
    )
  }

  return (
    <Container>
      <TopBar>
        <Typography variant="caption" color="primary">
          {t('selectedOfTotal', { selected: selectedIds.size, total: totalCount })}
        </Typography>
        {selectedIds.size > 0 && (
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => onChange(new Set())}
          >
            {t('clearAll')}
          </Link>
        )}
      </TopBar>
      <TextField
        size="small"
        placeholder={t('searchLocalities')}
        value={searchQuery}
        onChange={event => setSearchQuery(event.target.value)}
        inputProps={{ 'aria-label': t('searchLocalities') }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" aria-hidden />
            </InputAdornment>
          ),
        }}
        fullWidth
      />
      <AllLocalitiesRow onClick={handleAllToggle}>
        <Checkbox
          size="small"
          checked={allState === 'all'}
          indeterminate={allState === 'some'}
          onClick={event => event.stopPropagation()}
          onChange={handleAllToggle}
        />
        <Typography variant="body2" fontWeight={700}>
          {t('allLocalities')}
        </Typography>
        <CountChip hasSelection={selectedIds.size > 0} sx={{ marginLeft: 'auto' }}>
          {selectedIds.size} / {totalCount}
        </CountChip>
      </AllLocalitiesRow>
      <ScrollArea>
        {visibleSections.map(section => {
          const fullSection = sections.find(candidate => candidate.key === section.key)
          return (
            <SectionItem
              key={section.key}
              section={section}
              allLocalities={fullSection?.localities ?? section.localities}
              isExpanded={isSearching || expandedKeys.has(section.key)}
              selectedIds={selectedIds}
              onExpandToggle={() => handleExpandToggle(section.key)}
              onSectionToggle={() => handleSectionToggle(section.localities)}
              onLocalityToggle={handleLocalityToggle}
            />
          )
        })}
        {visibleSections.length === 0 && (
          <EmptyState>
            <Typography variant="body2">{t('noSearchResults', { query: debouncedSearchQuery.trim() })}</Typography>
          </EmptyState>
        )}
      </ScrollArea>
    </Container>
  )
}

interface SectionItemProps {
  section: ScopeSection
  allLocalities: ScopeLocality[]
  isExpanded: boolean
  selectedIds: Set<number>
  onExpandToggle: () => void
  onSectionToggle: () => void
  onLocalityToggle: (id: number) => void
}

const SectionItem: React.FC<SectionItemProps> = ({
  section,
  allLocalities,
  isExpanded,
  selectedIds,
  onExpandToggle,
  onSectionToggle,
  onLocalityToggle,
}) => {
  const { t } = useTranslation('reportGenerator')
  const visibleIds = section.localities.map(locality => locality.id)
  const state = getGroupSelectionState(selectedIds, visibleIds)
  const selectedCount = countSelectedIn(selectedIds, allLocalities)

  return (
    <SectionCard>
      <SectionHeader onClick={onExpandToggle}>
        <Checkbox
          size="small"
          checked={state === 'all'}
          indeterminate={state === 'some'}
          onClick={event => event.stopPropagation()}
          onChange={onSectionToggle}
        />
        <SectionName isUngrouped={section.isUngrouped}>
          {section.isUngrouped ? t('ungrouped') : section.name}
        </SectionName>
        <CountChip hasSelection={selectedCount > 0}>
          {selectedCount} / {allLocalities.length}
        </CountChip>
        <ExpandIcon isExpanded={isExpanded} fontSize="small" />
      </SectionHeader>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <SectionBody>
          {section.localities.map(locality => (
            <LocalityRow key={locality.id} onClick={() => onLocalityToggle(locality.id)}>
              <Checkbox
                size="small"
                checked={selectedIds.has(locality.id)}
                onClick={event => event.stopPropagation()}
                onChange={() => onLocalityToggle(locality.id)}
              />
              <Typography variant="body2">{locality.name}</Typography>
            </LocalityRow>
          ))}
        </SectionBody>
      </Collapse>
    </SectionCard>
  )
}

const Container = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TopBar = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const ScrollArea = styled(Box)`
  flex: 1;
  max-height: 560px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
`

const AllLocalitiesRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  marginRight: '4px',
  padding: '0 12px 6px 4px',
  cursor: 'pointer',
  userSelect: 'none',
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
}))

const SectionCard = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: '6px',
  overflow: 'hidden',
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 12px 4px 4px',
  cursor: 'pointer',
  userSelect: 'none',
  backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
}))

const SectionName = styled(Typography, { shouldForwardProp: prop => prop !== 'isUngrouped' })<{
  isUngrouped: boolean
}>(({ theme, isUngrouped }) => ({
  flex: 1,
  minWidth: 0,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...(isUngrouped && { fontStyle: 'italic', color: getThemeColor(theme, MUIThemeColors.secondaryDark) }),
}))

const CountChip = styled(Typography, { shouldForwardProp: prop => prop !== 'hasSelection' })<{
  hasSelection: boolean
}>(({ theme, hasSelection }) => ({
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: 600,
  padding: '1px 8px',
  borderRadius: '10px',
  fontVariantNumeric: 'tabular-nums',
  color: getThemeColor(theme, hasSelection ? MUIThemeColors.white : MUIThemeColors.secondaryDark),
  backgroundColor: getThemeColor(theme, hasSelection ? MUIThemeColors.primaryLight : MUIThemeColors.secondaryLight),
}))

const ExpandIcon = styled(ExpandMore, { shouldForwardProp: prop => prop !== 'isExpanded' })<{
  isExpanded: boolean
}>(({ theme, isExpanded }) => ({
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  transition: 'transform 0.2s',
  transform: isExpanded ? 'rotate(180deg)' : 'none',
}))

const SectionBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '2px 0 4px 20px',
  borderTop: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
}))

const LocalityRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.inputSearch),
  },
}))

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`
